/**
 * WorksheetQuoteForm.jsx - Build quote's Worksheet quote (10/12/26): the
 * GAT-B quote workbook in GlobalOps. Project info (the header the proposal
 * prints), then the workbook's own sheets (components/worksheet: rates and
 * expenses, engineers, the daily WorkSheet, Summary, Quote Data), the FCO
 * switch and the scope. Every number comes from the server; costs, COGS and
 * margin only for the 'cost-rates' area. The Word / PDF proposal never
 * carries a cost.
 */
import WorksheetEditor from '../worksheet/WorksheetEditor';
import { Field, Grid, NumInput, Section, SelectInput, TextInput, inputStyle } from './quoteUi';

export default function WorksheetQuoteForm({ draft, setDraft, settings, calc, canOverride, staff, seesCosts }) {
  const h = draft.header || {};
  const fco = h.fco === true;
  const lists = settings.lists || {};
  const setH = (k) => (v) => setDraft((d) => ({ ...d, header: { ...d.header, [k]: v } }));
  const setWs = (fn) => setDraft((d) => ({ ...d, worksheet: typeof fn === 'function' ? fn(d.worksheet || {}) : fn }));
  const rateCustomer = String((draft.worksheet && draft.worksheet.customer) || '').toLowerCase();
  const forCustomer = (list) => {
    const all = (list || []).filter((x) => x.name);
    // the rate card customer's own people first
    const mine = rateCustomer
      ? all.filter((x) =>
          String(x.customer || '')
            .toLowerCase()
            .includes(rateCustomer)
        )
      : [];
    return [...new Set([...mine, ...all].map((x) => x.name))];
  };
  return (
    <div>
      <Section title="Project info" hint="Prefilled from the opportunity and its account. Anything can be overtyped.">
        <Grid cols={4}>
          <Field label="Offer No.">
            <TextInput value={h.offerNo} onChange={setH('offerNo')} />
          </Field>
          <Field label="Offer date">
            <TextInput type="date" value={h.offerDate} onChange={setH('offerDate')} />
          </Field>
          <Field label="RFQ / project #">
            <TextInput value={h.rfqNo} onChange={setH('rfqNo')} />
          </Field>
          <Field label="Revision">
            <TextInput value={`R${h.rev || 0}`} disabled onChange={() => {}} />
          </Field>
          <Field label="Project description (subject)" span={2}>
            <TextInput value={h.subject} onChange={setH('subject')} />
          </Field>
          <Field label="Document type">
            {fco ? (
              <TextInput value="FIELD CHANGE ORDER" disabled onChange={() => {}} />
            ) : (
              <SelectInput value={h.kicker} options={lists.kickers || []} onChange={setH('kicker')} />
            )}
          </Field>
          <Field label="PO type">
            <SelectInput value={h.poType} options={lists.poTypes || ['T&L', 'Fixed Fee', 'Mixed']} onChange={setH('poType')} />
          </Field>
          <Field label="Customer" span={2}>
            <TextInput value={h.customer} onChange={setH('customer')} />
          </Field>
          <Field label="Address line 1">
            <TextInput value={h.addr1} onChange={setH('addr1')} />
          </Field>
          <Field label="City, ST ZIP">
            <TextInput value={h.addr2} onChange={setH('addr2')} />
          </Field>
          <Field label="End user">
            <TextInput value={h.endUser} onChange={setH('endUser')} />
          </Field>
          <Field label="Engineering manager">
            <TextInput list="ws-eng-managers" value={h.engManager} onChange={setH('engManager')} />
            <datalist id="ws-eng-managers">
              {forCustomer(settings.engManagers).map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </Field>
          <Field label="Project manager">
            <TextInput list="ws-project-managers" value={h.pm} onChange={setH('pm')} />
            <datalist id="ws-project-managers">
              {forCustomer(settings.projectManagers).map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </Field>
          <Field label="PO number (if issued)">
            <TextInput value={h.poNumber} onChange={setH('poNumber')} />
          </Field>
          <Field label="Salutation (Dear ...)">
            <TextInput value={h.salutation} onChange={setH('salutation')} placeholder="Sir or Madam" />
          </Field>
          <Field label="Valid (days)">
            <NumInput value={h.validity} onChange={setH('validity')} />
          </Field>
          <Field label="Payment terms" span={2}>
            <TextInput value={h.payTerms} onChange={setH('payTerms')} />
          </Field>
          <Field label="Invoicing">
            <TextInput value={h.invoicing} onChange={setH('invoicing')} placeholder="monthly" />
          </Field>
          <Field label="Prepared by">
            <TextInput value={h.preparer} onChange={setH('preparer')} />
          </Field>
          <Field label="Preparer phone">
            <TextInput value={h.prepPhone} onChange={setH('prepPhone')} />
          </Field>
          <Field label="Preparer email" span={2}>
            <TextInput value={h.prepEmail} onChange={setH('prepEmail')} />
          </Field>
        </Grid>
      </Section>

      <WorksheetEditor
        ws={draft.worksheet || {}}
        setWs={setWs}
        settings={settings}
        staff={staff}
        calc={calc && calc.type === 'worksheet' ? calc : null}
        seesCosts={seesCosts}
        canOverride={canOverride}
      />

      <Section
        title="Field change order (FCO)"
        hint="For a change to a job already quoted: the document says FCO, and the previous overages print as their own row and are in the TOTAL BUDGET."
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: fco ? 10 : 0,
          }}
        >
          <input type="checkbox" checked={fco} onChange={(e) => setH('fco')(e.target.checked)} />
          This is an FCO (field change order)
        </label>
        {fco && (
          <Grid cols={4}>
            <Field label="Previous overages ($)">
              <NumInput aria-label="Previous overages" value={h.prevOverages} placeholder="0.00" onChange={setH('prevOverages')} />
            </Field>
            <Field label="Changes to quote #">
              <TextInput aria-label="Changes to quote" value={h.fcoRef} onChange={setH('fcoRef')} placeholder="e.g. GAT-B-7101" />
            </Field>
            <Field label="What the overages cover (optional)" span={2}>
              <TextInput aria-label="What the overages cover" value={h.overagesNote} onChange={setH('overagesNote')} />
            </Field>
          </Grid>
        )}
      </Section>

      <Section title="Scope of work" hint="Printed as section 1.0. Each line is its own paragraph.">
        <textarea
          aria-label="Scope of work"
          value={draft.scope || ''}
          rows={6}
          onChange={(e) => {
            const v = e.target.value;
            setDraft((d) => ({ ...d, scope: v }));
          }}
          placeholder="Scope of work per the customer request."
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.45 }}
        />
      </Section>
    </div>
  );
}
